import React, { useEffect } from 'react';
import { Home, Building, Hammer, Eye, Target } from 'lucide-react';

const Services = () => {
    useEffect(() => {
        window.scrollTo(0, 0);
    }, []);

    const services = [
        {
            id: "residential",
            title: "Residential Construction",
            icon: <Home className="w-12 h-12 text-primary" />,
            description: "We craft homes that are a perfect blend of comfort, style, and functionality. From cozy apartments to luxurious villas, our residential projects are designed to provide a serene and modern living experience.",
            features: ["Custom Home Design", "Premium Finishes", "Vastu Compliant Layouts", "Sustainable Building Practices"],
            image: "/assets/photos/elevations/P_10.jpg"
        },
        {
            id: "commercial",
            title: "Commercial Development",
            icon: <Building className="w-12 h-12 text-primary" />,
            description: "Our commercial spaces are built to empower businesses. We understand the changing dynamics of the corporate world and create infrastructure that boosts productivity and growth.",
            features: ["Modern Office Spaces", "Retail Complexes", "Strategic Locations", "High-Tech Infrastructure"],
            image: "https://images.unsplash.com/photo-1497366216548-37526070297c?auto=format&fit=crop&q=80&w=800"
        },
        {
            id: "redevelopment",
            title: "Redevelopment",
            icon: <Hammer className="w-12 h-12 text-primary" />,
            description: "Revitalizing old structures into contemporary landmarks. We specialize in redevelopment projects that enhance the value of the property while preserving its original charm where needed.",
            features: ["Structural Strengthening", "Modern Facade Upgrades", "Space Optimization", "Legal Compliance"],
            image: "https://images.unsplash.com/photo-1533090161767-e6ffed986c88?auto=format&fit=crop&q=80&w=800"
        }
    ];

    return (
        <div className="bg-white min-h-screen pt-10 pb-20">
            <div className="container mx-auto px-4 mb-16 text-center">
                <h1 className="text-4xl font-bold text-gray-800 mb-4">Our Services</h1>
                <p className="text-gray-600 max-w-2xl mx-auto">
                    Comprehensive construction solutions tailored to your unique needs.
                </p>
            </div>

            <div className="container mx-auto px-4 space-y-20">
                {services.map((service, index) => (
                    <div
                        key={service.id}
                        className={`flex flex-col ${index % 2 === 1 ? 'lg:flex-row-reverse' : 'lg:flex-row'} items-center gap-12`}
                    >
                        {/* Image Side */}
                        <div className="w-full lg:w-1/2">
                            <div className="relative group">
                                <div className="absolute inset-0 bg-primary/10 transform translate-x-4 translate-y-4 rounded-xl -z-10 group-hover:translate-x-2 group-hover:translate-y-2 transition-transform duration-300"></div>
                                <img
                                    src={service.image}
                                    alt={service.title}
                                    className="rounded-xl shadow-lg w-full h-[400px] object-cover"
                                />
                            </div>
                        </div>

                        {/* Text Side */}
                        <div className="w-full lg:w-1/2">
                            <div className="mb-6">{service.icon}</div>
                            <h2 className="text-3xl font-bold text-gray-800 mb-6">{service.title}</h2>
                            <p className="text-gray-600 text-lg leading-relaxed mb-8">
                                {service.description}
                            </p>

                            <ul className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                                {service.features.map((feature, idx) => (
                                    <li key={idx} className="flex items-center text-gray-700 font-medium">
                                        <span className="w-2 h-2 bg-primary rounded-full mr-3"></span>
                                        {feature}
                                    </li>
                                ))}
                            </ul>
                        </div>
                    </div>
                ))}
            </div>


            {/* Philosophy Section */}
            <div className="py-20 bg-white border-t border-gray-100">
                <div className="container mx-auto px-4 max-w-4xl text-center">
                    <h2 className="text-3xl font-bold text-gray-800 mb-8">Our Philosophy</h2>
                    <div className="text-xl text-gray-700 leading-relaxed font-light italic space-y-6">
                        <p>
                            "Joshi Construction recognizes that the focal space of happy living is one’s abode. One’s abode is one’s temple, one’s place of worship. The divinely blessed abode or man-made universe is one that is alive, self-sustaining, content and whole."
                        </p>
                        <p>
                            "Therefore the sanctity of the living space must be maintained in line with the universal laws governing peaceful coexistence. Form follows function, while catering to human needs like safety, comfort, and a sense of belonging and identity. Bringing this divinity into architecture, planning and building is the forte of Joshi Construction, a master in creating harmonious residential spaces."
                        </p>
                    </div>
                </div>
            </div>
            {/* Vision & Mission S ection */}
            <div className="bg-gray-50 py-20 mt-20">
                <div className="container mx-auto px-4">
                    <div className="grid grid-cols-1 md:grid-cols-2 gap-12 max-w-5xl mx-auto">
                        {/* Vision */}
                        <div className="bg-white p-10 rounded-2xl shadow-lg hover:shadow-xl transition-shadow duration-300 border-t-4 border-primary">
                            <div className="flex items-center mb-6">
                                <div className="p-3 bg-orange-100 rounded-lg text-primary mr-4">
                                    <Eye size={32} />
                                </div>
                                <h2 className="text-3xl font-bold text-gray-800">Our Vision</h2>
                            </div>
                            <p className="text-gray-600 leading-relaxed text-lg">
                                Deliver comprehensive and effective solutions to clients through our profound experience and technological prowess, while continuously creating opportunities and possibilities for employees and society.
                            </p>
                        </div>

                        {/* Mission */}
                        <div className="bg-white p-10 rounded-2xl shadow-lg hover:shadow-xl transition-shadow duration-300 border-t-4 border-gray-800">
                            <div className="flex items-center mb-6">
                                <div className="p-3 bg-gray-100 rounded-lg text-gray-800 mr-4">
                                    <Target size={32} />
                                </div>
                                <h2 className="text-3xl font-bold text-gray-800">Our Mission</h2>
                            </div>
                            <ul className="space-y-4">
                                {[
                                    "To deliver projects exceeding customer expectations.",
                                    "To develop a highly skilled and motivated team of professionals.",
                                    "To constantly adapt innovative methods and latest technologies."
                                ].map((item, idx) => (
                                    <li key={idx} className="flex items-start text-gray-600 text-lg">
                                        <span className="w-2 h-2 bg-primary rounded-full mt-2.5 mr-3 flex-shrink-0"></span>
                                        <span>{item}</span>
                                    </li>
                                ))}
                            </ul>
                        </div>
                    </div>
                </div>
            </div>

            {/* Quality Policy & Customer Focus */}
            <div className="bg-white py-20">
                <div className="container mx-auto px-4">
                    <div className="grid grid-cols-1 md:grid-cols-2 gap-12 max-w-5xl mx-auto">
                        {/* Quality Policy */}
                        <div className="bg-gray-50 p-10 rounded-2xl shadow-lg hover:shadow-xl transition-shadow duration-300 border-t-4 border-primary">
                            <h2 className="text-3xl font-bold text-gray-800 mb-6">Quality Policy</h2>
                            <p className="text-gray-600 leading-relaxed text-lg mb-4">
                                "We at Joshi Construction deliver quality and cost-effective products and services that contribute to our customer's success in all our work. We understand and not only meet but exceed our customer's requirements and satisfaction."
                            </p>
                            <p className="text-gray-600 leading-relaxed text-lg">
                                "We continuously improve our products, services and processes to achieve operational excellence. We respect and hold sacred relationships with customers, suppliers and employees."
                            </p>
                        </div>

                        {/* Customer Focus */}
                        <div className="bg-gray-50 p-10 rounded-2xl shadow-lg hover:shadow-xl transition-shadow duration-300 border-t-4 border-gray-800">
                            <h2 className="text-3xl font-bold text-gray-800 mb-6">Customer Focus</h2>
                            <p className="text-gray-600 leading-relaxed text-lg mb-4">
                                "We put customer service at the top of our priority list from day one. We ensure timely delivery of services to customers. We establish lasting relationships with customers by exceeding expectations through integrity, innovation and commitment."
                            </p>
                            <p className="text-gray-600 leading-relaxed text-lg">
                                "We provide a balanced economic value for investors and property owners through quality construction and best practice techniques."
                            </p>
                        </div>
                    </div>
                </div>
            </div>
        </div >
    );
};

export default Services;
